/* ══════════════════════════════════════════════════════════════════
   Die alten Adressen unter `/handy…` — sie führen alle auf `/heute`.

   Luca, 19.09.2026: *„Überleg dir mal, ob diese /handy-URL … macht ja gar
   keinen Sinn."* Seit dem 17.09.2026 entscheidet das Gerät über die Fassung
   und nicht die Adresse (`schale/geraet.ts`); die Wege heißen seitdem nach
   der Aufgabe: `/heute`, `/einsatz/:belegung`, `/aufnahmen/:id`.

   ── Warum hier trotzdem noch etwas steht — 21.09.2026 ─────────────

   Luca: *„Wozu brauchen wir die Weiterleitungen, das benutzt doch keiner
   oder wie?"* Nachgesehen, und es gibt **genau einen** Fall, der sie
   braucht:

   ```
   git show 0c8fffd:apps/web/public/site.webmanifest
     "id": "/handy",  "start_url": "/handy",
   ```

   Zwischen dem 16. und dem 17.09.2026 stand `/handy` als `start_url` im
   Manifest. **Ein iPhone übernimmt ein geändertes Manifest nicht**
   (40-mobiles-grundgeruest.md, Schritt 5): Wer die Testfassung in diesem
   Fenster auf den Startbildschirm gelegt hat, startet seine Kachel bis
   heute auf `/handy`. Das betrifft niemanden draußen — aber möglicherweise
   genau die zwei Telefone, auf denen geprüft wird.

   ── Was deshalb weg ist ───────────────────────────────────────────

   Die Tabelle mit fünfzehn Übersetzungen (`/handy/einsatz/:belegung/…`,
   `/handy/aufnahmen/:id`, `/handy/angebot?vorgang=…`, `/handy/kunden/:nr`
   und so weiter). Sie stand für Lesezeichen und geteilte Links auf
   **einzelne** Masken — vier Tage lang, in einer Testfassung, die niemand
   teilt. Eine Kachel wird gelegt, ein Tiefenlink nicht.

   Geblieben ist die eine Regel: **alles unter `/handy` führt auf `/heute`**,
   den Einstieg. Wer doch einen alten Tiefenlink hat, landet auf dem Schirm,
   von dem aus jeder Weg abgeht, statt im Nichts — und das war schon vorher
   die Begründung: „Eine Adresse, die ins Leere läuft, ist schlimmer als eine,
   die woanders ankommt."

   **Die Manifest-`id` bleibt `"/handy"`.** Sie ist keine Adresse, sondern der
   Name der installierten App (`public/site.webmanifest`); wer sie ändert,
   macht aus der Kachel auf dem Startbildschirm eine zweite.

   Im Routenbaum steht für `/handy/…` ein Auffangweg ohne Maske (`/handy/$`),
   damit ein vergessener Verweis im Code (`to="/handy/einsatz/$belegung"`)
   ein **Typfehler** ist und keine Umleitung, die niemand bemerkt.

   Die Funktion ist rein — kein Router, kein Fenster —, damit sie sich ohne
   Browser prüfen lässt.
   ══════════════════════════════════════════════════════════════════ */

/** Liegt der Pfad unter der alten Wurzel `/handy`? */
export function istAlteAdresse(pfad: string): boolean {
  return pfad === "/handy" || pfad.startsWith("/handy/");
}

/* ── Die Anmeldung als Adresse — N5-03, 04.10.2026 ──────────────────

   Die Anmeldung hat keine eigene Adresse: Wer nicht angemeldet ist, sieht
   sie auf **jedem** Weg (`schale/Schale.tsx`), und nach dem Anmelden steht
   die Maske dieses Wegs da — so landet, wer `/planung` aufgerufen hat, nach
   der Anmeldung auf der Planung. Wer aber `/anmeldung` aufruft (abgetippt,
   Lesezeichen, Mia im Durchgang am 04.10.), stand danach auf „Diese Adresse
   gibt es nicht" — nach dem Einrichten des zweiten Faktors genauso wie nach
   jeder anderen Anmeldung. Diese Wege führen deshalb auf den Einstieg, wie
   die alten unter `/handy`: Die Anmeldung zeichnet sich darüber ohnehin. */
const ANMELDEWEGE = new Set(["/anmeldung", "/anmelden", "/login"]);

/** Ist der Pfad ein Name für die Anmeldung (`/anmeldung`, `/login`)? */
export function istAnmeldeweg(pfad: string): boolean {
  return ANMELDEWEGE.has(pfad.replace(/\/+$/, "") || "/");
}

/**
 * Wohin eine alte Adresse heute führt — oder `null`, wenn es keine alte ist.
 *
 * Eine Antwort für alle: `/heute`. Der Einstieg trägt seit dem 21.09.2026
 * beide Hälften (`masken/Heute.tsx`), und von dort geht jeder Weg ab.
 */
export function neueAdresse(pfad: string): string | null {
  return istAlteAdresse(pfad) || istAnmeldeweg(pfad) ? "/heute" : null;
}
